import axios from 'axios';

const apiClient = axios.create({
    // 本番環境では環境変数から、ローカルではproxyを使うため'/'を指定
    baseURL: process.env.REACT_APP_API_URL || '/',
});

// リクエストインターセプター
// 全てのリクエストが送信される前に、この処理が実行される
apiClient.interceptors.request.use(
    (config) => {
        // ログインリクエストの場合は、既存のトークンを付与しない
        if (config.url === '/api/auth/token-login') {
            return config;
        }

        // localStorageから認証トークンを取得
        const token = localStorage.getItem('authToken');
        
        // トークンが存在すれば、Authorizationヘッダーに付与
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        return config;
    },
    (error) => {
        // リクエストエラーの処理
        return Promise.reject(error);
    }
);

let isSessionExpired = false; // セッション切れのフラグ

// レスポンスインターセプター
// 全てのレスポンスが返ってきた後に、この処理が実行される
apiClient.interceptors.response.use(
    (response) => {
        // レスポンスヘッダーから新しいトークンを確認
        const newToken = response.headers['x-refreshed-token'];
        if (newToken) {
            // localStorageのトークンを更新
            localStorage.setItem('authToken', newToken);
        }
        // 正常なレスポンスはそのまま返す
        return response;
    },
    (error) => {
        // エラーレスポンスの処理
        if (error.response && error.response.status === 401) {
            if (!isSessionExpired) {
                isSessionExpired = true; // フラグを立てる
                // 401 Unauthorizedエラーの場合、カスタムイベントを発行
                window.dispatchEvent(new Event('unauthorized'));
            }
        }
        return Promise.reject(error);
    }
);

// リトライ設定
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_INITIAL_DELAY = 1000; // 1秒

/**
 * リトライ可能なエラーかどうかを判定
 */
function isRetryableError(error: any): boolean {
    // ネットワークエラー（レスポンスがない場合）
    if (!error.response) {
        return true;
    }

    // 5xx サーバーエラー（500, 502, 503, 504など）
    if (error.response.status >= 500 && error.response.status < 600) {
        return true;
    }

    // 429 Too Many Requests
    if (error.response.status === 429) {
        return true;
    }

    // 408 Request Timeout
    if (error.response.status === 408) {
        return true;
    }

    return false;
}

/**
 * 待機時間を計算（Exponential backoff with jitter）
 */
function calculateDelay(attempt: number, initialDelay: number): number {
    const exponentialDelay = initialDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 1000; // 0-1000msのランダムな遅延
    return exponentialDelay + jitter;
}

/**
 * リトライ機能付きのPOSTリクエスト
 */
export async function postWithRetry<T = any>(
    url: string,
    data?: any,
    maxRetries: number = DEFAULT_MAX_RETRIES,
    initialDelay: number = DEFAULT_INITIAL_DELAY
): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            const response = await apiClient.post<T>(url, data);
            if (attempt > 0) {
                console.log(`Request succeeded on attempt ${attempt + 1}`);
            }
            return response.data;
        } catch (error: any) {
            lastError = error;

            // リトライ可能なエラーかどうかを判定
            if (isRetryableError(error) && attempt < maxRetries - 1) {
                const delay = calculateDelay(attempt, initialDelay);
                console.warn(
                    `Request failed (attempt ${attempt + 1}/${maxRetries}): ${error.message}. Retrying in ${Math.round(delay)}ms...`
                );
                await new Promise((resolve) => setTimeout(resolve, delay));
            } else {
                // リトライ不可能なエラー、またはリトライ回数超過
                if (attempt === maxRetries - 1) {
                    console.error(`Request failed after ${maxRetries} attempts`);
                }
                throw error;
            }
        }
    }

    throw lastError;
}

export default apiClient;
